import React, { useState, useEffect } from 'react';
import MUIDataTable from 'mui-datatables';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import styled from 'styled-components';
import { Printer, Download, SlidersHorizontal, Columns3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import { logClientExport } from '../../services/clientExportAudit';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const Wrapper = styled.div`
  /* Paper */
  .MuiPaper-root {
    box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06) !important;
    border-radius: 6px !important;
  }

  /* NO UPPERCASE anywhere */
  .MuiTableCell-head,
  .MuiButton-root,
  .MuiToolbar-root,
  .MuiChip-root,
  .MuiTypography-root {
    text-transform: none !important;
  }

  /* Header cells */
  .MuiTableCell-head {
    font-family: 'Nunito Sans', sans-serif !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    color: #004242 !important;
    background: #fff !important;
    padding: 10px 14px !important;
  }

  /* Border under header row */
  .MuiTableCell-head {
    border-bottom: 2px solid #0291EF !important;
  }

  /* Body cells */
  .MuiTableCell-body {
    font-family: 'Nunito Sans', sans-serif !important;
    font-size: 13px !important;
    color: #004242 !important;
    padding: 10px 14px !important;
  }

  .MuiTableRow-root {
    cursor: pointer;
  }
  .MuiTableRow-root:hover {
    background: #F5F7FA !important;
  }

  /* Toolbar */
  .MuiToolbar-root {
    font-family: 'Nunito Sans', sans-serif !important;
    min-height: 52px !important;
    padding: 0 14px !important;
    background: #fff !important;
  }

  /* Title in toolbar */
  .MuiToolbar-root .MuiTypography-root {
    font-family: 'Nunito Sans', sans-serif !important;
    font-size: 18px !important;
    font-weight: 700 !important;
    color: #004242 !important;
  }

  /* Buttons & chips */
  .MuiButton-root,
  .MuiChip-root {
    font-family: 'Nunito Sans', sans-serif !important;
    border-radius: 4px !important;
  }

  /* Pagination */
  .MuiTablePagination-root {
    font-family: 'Nunito Sans', sans-serif !important;
  }
  .MuiTablePagination-selectLabel,
  .MuiTablePagination-displayedRows,
  .MuiTablePagination-select {
    font-family: 'Nunito Sans', sans-serif !important;
    font-size: 12px !important;
  }

  /* Checkbox */
  .MuiCheckbox-root {
    color: #0291EF !important;
  }

  /* Selected rows toolbar — only when rows are selected */
  [class*="MUIDataTableToolbarSelect"] {
    background: rgba(2, 145, 239, 0.06) !important;
  }

  /* Filter styles are in global.js (MUI renders filter dialog as portal on body) */
`;

const muiTheme = createTheme({
  typography: {
    fontFamily: "'Nunito Sans', sans-serif",
    button: { textTransform: 'none' },
  },
  palette: {
    primary: { main: '#0291EF' },
    secondary: { main: '#02D3BF' },
  },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', borderRadius: 4 } } },
    MuiChip: { styleOverrides: { root: { textTransform: 'none' } } },
    MuiTableCell: { styleOverrides: { head: { textTransform: 'none' } } },
    MuiSelect: {
      styleOverrides: {
        root: { fontFamily: "'Nunito Sans', sans-serif", fontSize: 13 },
        select: { fontFamily: "'Nunito Sans', sans-serif" },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontFamily: "'Nunito Sans', sans-serif", fontSize: 13 },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontFamily: "'Nunito Sans', sans-serif", fontSize: 13 },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: { fontFamily: "'Nunito Sans', sans-serif", fontSize: 13 },
      },
    },
  },
});

export default function DataTable({
  columns,
  data,
  totalRowCount = 0,
  pageSize = 10,
  pageIndex = 0,
  onTableChanged,
  onFilterConfirm,
  // Kabul ediliyor ama kurulu mui-datatables@4.3.0 tarafından hiç okunmuyor (bkz.
  // Görev 9 incelemesi) — chip/filtre durumu için MUIDataTable'a iletilmiyor.
  filterList,
  canCreateNewRecord,
  newButtonLabel,
  // Düğme metninin önündeki "+ " oluşturma işaretidir. Eylem bir kayıt OLUŞTURMUYORSA (ör. Entegrasyonlar >
  // Test Ortamı'ndaki "Test Ortamına Git") çağıran false verir; varsayılan davranış değişmez.
  newButtonPlusSign = true,
  toolbarActions,
  onNewButtonClick,
  onRowsDelete,
  // PM Analitik Faz 2 Plan 4 (spec §7.3) — seçim araç çubuğu düğmeleri: [{ key, label | (rows) => label, onClick(rows),
  // disabled?(rows) }]. `rows` seçili satırların veri nesneleridir (seçim yalnız mevcut sayfadadır; veri değişince sıfırlanır).
  // Verilmezse davranış AYNEN eski: seçim yalnız onRowsDelete ile açılır ve kütüphanenin silme çubuğu çizilir.
  selectionActions,
  onRowClick,
  fetching,
  title,
  customIdSelector = 'id',
  tableBodyMaxHeight,
  disableFilters = false,
  serverSide = true,
  onDownload,
  // AKM POC §7.1 — sunucu tarafı tam veri dışa aktarması olan sayfalar yerleşik "yalnız ekrandaki sayfa" CSV'sini
  // kapatır (anti-hedef): download={false}.
  download = true,
  // AKM POC §7.6 — satır açılınca ek içerik (puantaj satırında o günün giriş-çıkışları). renderExpandableRow(row) →
  // düğüm; tablo genişliğinde tek hücreye sarılır.
  expandableRows = false,
  renderExpandableRow,
}) {
  const { t } = useTranslation();
  const [rowsSelected, setRowsSelected] = useState([]);
  const handlePrint = () => {
    window.print();
  };

  // Reset selection when data changes (after delete, page change, etc.)
  useEffect(() => { setRowsSelected([]); }, [data]);

  const options = {
    serverSide,
    count: serverSide ? totalRowCount : undefined,
    page: serverSide ? pageIndex : undefined,
    rowsPerPage: pageSize,
    // Sayfanın varsayılan boyu standart seçeneklerde yoksa (ör. İzin Bakiye Raporu 25) seçenek listesine eklenir —
    // eklenmezse "sayfa başına satır" seçicisi boş görünüyordu (2026-10-20).
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS.includes(pageSize)
      ? ROWS_PER_PAGE_OPTIONS
      : [...ROWS_PER_PAGE_OPTIONS, pageSize].sort((a, b) => a - b),
    filterType: 'textField',
    confirmFilters: true,
    selectableRows: (onRowsDelete || selectionActions) ? 'multiple' : 'none',
    rowsSelected,
    onRowSelectionChange: (_, allSelected) => setRowsSelected(allSelected.map((r) => r.dataIndex)),
    responsive: 'standard',
    tableBodyMaxHeight: tableBodyMaxHeight || 'calc(100vh - 280px)',
    download,
    print: true,
    expandableRows: Boolean(expandableRows && renderExpandableRow),
    expandableRowsOnClick: false,
    renderExpandableRow: expandableRows && renderExpandableRow
      ? (rowData, rowMeta) => (
        <TableRow>
          <TableCell colSpan={rowData.length + 1}>
            {renderExpandableRow(data[rowMeta.dataIndex], rowMeta)}
          </TableCell>
        </TableRow>
      )
      : undefined,
    viewColumns: true,
    filter: !disableFilters,
    search: false,
    jumpToPage: false,
    elevation: 0,
    textLabels: {
      body: {
        noMatch: fetching ? t('common.loading') : t('common.noRecords'),
        toolTip: t('dataTable.sort'),
      },
      pagination: {
        next: t('dataTable.next'),
        previous: t('dataTable.previous'),
        rowsPerPage: t('dataTable.rowsPerPage'),
        displayRows: '/',
      },
      toolbar: {
        search: t('common.search'),
        downloadCsv: t('dataTable.downloadCsv'),
        viewColumns: t('dataTable.viewColumns'),
        filterTable: t('dataTable.filter'),
      },
      filter: {
        all: t('dataTable.all'),
        title: t('dataTable.filters'),
        reset: t('dataTable.reset'),
      },
      selectedRows: {
        text: t('dataTable.rowsSelected'),
        delete: t('common.delete'),
        deleteAria: t('dataTable.deleteSelected'),
      },
    },

    // `toolbarActions`: sayfaya özel düğmeler tablonun KENDİ araç çubuğunda,
    // yerleşik ikonların (indir/yazdır/kolonlar/filtre) yanında ve "Yeni"
    // düğmesinin hemen solunda durur. Sayfanın tablo dışına ayrı bir şerit
    // koymasına gerek kalmaz (müşteri geri bildirimi 2026-08-12).
    customToolbar: (toolbarActions || canCreateNewRecord)
      ? () => (
        <>
          {toolbarActions}
          {canCreateNewRecord && (
            <Button onClick={onNewButtonClick}>{newButtonPlusSign ? '+ ' : ''}{newButtonLabel || t('dataTable.new')}</Button>
          )}
        </>
      )
      : undefined,

    customFilterDialogFooter: (currentFilterList, applyNewFilters) => (
      <FilterFooter>
        <Button onClick={() => applyNewFilters()}>{t('dataTable.applyFilters')}</Button>
      </FilterFooter>
    ),

    onFilterConfirm: (filterList) => {
      onFilterConfirm?.(filterList);
    },

    onTableChange: (action, tableState) => {
      if (['changePage', 'changeRowsPerPage', 'sort', 'filterChange'].includes(action)) {
        onTableChanged?.(action, tableState);
      }
    },

    customToolbarSelect: selectionActions
      ? (selected) => {
          const rows = (selected?.data || []).map((d) => data[d.dataIndex]).filter(Boolean);
          return (
            <SelectionBar>
              {selectionActions.map((a) => (
                <Button
                  key={a.key}
                  variant="outline"
                  data-selection-action={a.key}
                  disabled={a.disabled ? a.disabled(rows) : false}
                  onClick={() => a.onClick(rows)}
                >
                  {typeof a.label === 'function' ? a.label(rows) : a.label}
                </Button>
              ))}
              {onRowsDelete ? (
                <Button
                  variant="outline"
                  data-selection-action="delete"
                  onClick={() => onRowsDelete(rows.map((r) => r[customIdSelector] || r.id).filter(Boolean))}
                >
                  {t('common.delete')}
                </Button>
              ) : null}
            </SelectionBar>
          );
        }
      : undefined,

    onRowsDelete: onRowsDelete
      ? (rowsDeleted) => {
          const ids = rowsDeleted.data.map((d) => data[d.dataIndex]?.[customIdSelector] || data[d.dataIndex]?.id);
          onRowsDelete(ids.filter(Boolean));
          return false;
        }
      : undefined,

    onRowClick: onRowClick
      ? (rowData, rowMeta) => {
          const row = data[rowMeta.dataIndex];
          if (row) onRowClick(row);
        }
      : undefined,

    onDownload: onDownload
      ? (buildHead, buildBody, cols, rows) => onDownload(buildHead, buildBody, cols, rows, title)
      : (buildHead, buildBody, cols, rows) => {
          const BOM = '\uFEFF';
          const csv = buildHead(cols) + buildBody(rows);
          const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${title || 'export'}.csv`;
          link.click();
          // AKM POC §9.1 — tarayıcıda üretilen dosya da dışa aktarma olayıdır; indirmeyi beklemez, hata gösterilmez.
          logClientExport({
            reportKey: (window.location.pathname || '/').slice(0, 128),
            title: typeof title === 'string' ? title.slice(0, 200) : undefined,
            rowCount: Array.isArray(rows) ? rows.length : 0,
          });
          return false;
        },
  };

  return (
    <MuiThemeProvider theme={muiTheme}>
      <Wrapper>
        <MUIDataTable
          title={title || ''}
          data={data}
          columns={columns}
          options={options}
          components={{
            icons: {
              FilterIcon: () => <SlidersHorizontal size={18} />,
              ViewColumnIcon: () => <Columns3 size={18} />,
              DownloadIcon: () => <Download size={18} />,
              PrintIcon: () => <Printer size={18} />,
            },
          }}
        />
      </Wrapper>
    </MuiThemeProvider>
  );
}

// #72 (UI düzeltme demeti T6) — yerel NewButton/ApplyButton (kendi gradyan
// tarifi — 120deg, radius 4px) kaldırıldı, paylaşılan Button'a bağlandı
// (135deg BİREBİR aynı gradyan kimliği, radius 8px — bkz. t6-report.md).
// BU DOSYA neredeyse HER liste sayfasının "+ Yeni" ve filtre "Uygula"
// butonunu üretiyor — tek bir değişiklik uygulamanın TAMAMINA yayılıyor.

const SelectionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-right: 16px;
`;

const FilterFooter = styled.div`
  padding: 12px 24px;
  display: flex;
  justify-content: flex-end;
`;

